import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Alert, Chip, Checkbox, Tooltip,
  LinearProgress, Divider, IconButton
} from '@mui/material';
import {
  Save as SaveIcon, Refresh, Upload as UploadIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { marksAPI, assessmentsAPI, settingsAPI, subjectsAPI, teachersAPI } from '../services/api';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function MarksEntry() {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const { user } = useAuth();
  const urlParams = new URLSearchParams(location.search);
  const initAssessmentId = urlParams.get('assessment_id') || '';

  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assessments, setAssessments] = useState([]);

  const [filterYear, setFilterYear] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState(initAssessmentId);

  const [entrySheet, setEntrySheet] = useState(null);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [assignments, setAssignments] = useState([]); // teacher's class-subject assignments
  const isTeacher = !!user?.teacher_id;

  // Bootstrap dropdowns
  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setFilterYear(cur.id);
      }
    }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    subjectsAPI.getAll().then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});
    teachersAPI.getMyAssignments().then(r => { if (r.data.success) setAssignments(r.data.data); }).catch(() => {});
  }, []);

  // Load assessments when year/class/subject filter changes
  useEffect(() => {
    if (!filterYear) return;
    assessmentsAPI.getAll({
      academic_year_id: filterYear || undefined,
      class_id: filterClass || undefined,
      subject_id: filterSubject || undefined,
    }).then(r => {
      if (r.data.success) setAssessments(r.data.data);
    }).catch(() => {});
  }, [filterYear, filterClass, filterSubject]);

  // Auto-load if assessment_id in URL
  useEffect(() => {
    if (initAssessmentId) {
      setSelectedAssessment(initAssessmentId);
      loadEntrySheet(initAssessmentId, filterClass);
    }
  }, [initAssessmentId]);

  // Filter classes/subjects for teachers
  const availableClasses = isTeacher
    ? classes.filter(c => assignments.some(a => a.class_id == c.id))
    : classes;

  const availableSubjects = isTeacher && filterClass
    ? assignments
        .filter(a => a.class_id == filterClass)
        .map(a => ({ id: a.subject_id, name: a.subject_name }))
    : subjects;

  const loadEntrySheet = async (assessmentId, classId) => {
    if (!assessmentId) return;
    setLoading(true); setError(''); setEntrySheet(null);
    try {
      const res = await marksAPI.getEntrySheet(assessmentId, classId);
      if (res.data.success) {
        setEntrySheet(res.data.data);
        const existing = {};
        res.data.data.students.forEach(s => {
          existing[s.student_id] = {
            score: s.score ?? '',
            is_absent: !!s.is_absent,
            remarks: s.remarks || ''
          };
        });
        setMarks(existing);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading entry sheet');
    } finally { setLoading(false); }
  };

  const handleMarkChange = (sid, field, value) => {
    setMarks(prev => ({ ...prev, [sid]: { ...prev[sid], [field]: value } }));
  };

  const handleSaveAll = async () => {
    if (!entrySheet) return;
    setSaving(true);
    try {
      const marksArray = entrySheet.students.map(s => ({
        student_id: s.student_id,
        score: marks[s.student_id]?.is_absent ? null
          : marks[s.student_id]?.score !== '' ? parseFloat(marks[s.student_id]?.score) : null,
        is_absent: marks[s.student_id]?.is_absent ? 1 : 0,
        remarks: marks[s.student_id]?.remarks || ''
      }));
      await marksAPI.bulkSave({ assessment_id: selectedAssessment, marks: marksArray });
      enqueueSnackbar('Marks saved successfully', { variant: 'success' });
      loadEntrySheet(selectedAssessment, filterClass);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving marks', { variant: 'error' });
    } finally { setSaving(false); }
  };

  // CSV import
  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedAssessment || !filterClass) {
      return enqueueSnackbar('Select an assessment and class first', { variant: 'warning' });
    }
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => !l.startsWith('#') && l.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',');
        const obj = {};
        headers.forEach((h, i) => { obj[h] = vals[i]?.trim() || ''; });
        return obj;
      }).filter(r => r.admission_number);

      const res = await axios.post(`${API}/import/marks`, {
        assessment_id: selectedAssessment, class_id: filterClass, rows
      });
      if (res.data.success) {
        enqueueSnackbar(res.data.message, { variant: 'success' });
        if (res.data.errors?.length) {
          console.warn('Import warnings:', res.data.errors);
        }
        loadEntrySheet(selectedAssessment, filterClass);
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Import failed', { variant: 'error' });
    } finally { setImporting(false); e.target.value = ''; }
  };

  const handleDownloadTemplate = () => {
    // Use axios so the Authorization header is sent, then trigger browser download
    axios.get(`${API}/import/template/marks`, {
      params: { assessment_id: selectedAssessment, class_id: filterClass },
      responseType: 'blob'
    }).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'marks_import_template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    }).catch(() => enqueueSnackbar('Error downloading template', { variant: 'error' }));
  };

  const a = entrySheet?.assessment;
  const maxScore = a?.max_score || 100;
  const stats = entrySheet?.stats;

  // Progress bar
  const progress = stats ? Math.round((stats.entered / (stats.total || 1)) * 100) : 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Marks Entry</Typography>

      {/* ── Filter Bar ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField select label="Academic Year" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField select label="Class" value={filterClass}
                onChange={e => { setFilterClass(e.target.value); setEntrySheet(null); setSelectedAssessment(''); }}>
                <MenuItem value="">All Classes</MenuItem>
                {availableClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField select label="Subject" value={filterSubject}
                onChange={e => { setFilterSubject(e.target.value); setEntrySheet(null); setSelectedAssessment(''); }}>
                <MenuItem value="">All Subjects</MenuItem>
                {availableSubjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Assessment" value={selectedAssessment}
                onChange={e => { setSelectedAssessment(e.target.value); if (e.target.value) loadEntrySheet(e.target.value, filterClass); }}>
                <MenuItem value="">Select Assessment</MenuItem>
                {assessments.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.title} — {a.subject_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="outlined" startIcon={<Refresh />} fullWidth
                onClick={() => loadEntrySheet(selectedAssessment, filterClass)}
                disabled={!selectedAssessment || loading}>
                Reload
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ mb: 2 }}><LinearProgress /></Box>}

      {/* ── Entry Sheet ── */}
      {entrySheet && (
        <>
          {/* Header info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{a.title}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip label={a.subject_name} size="small" color="primary" />
                <Chip label={`Max: ${a.max_score}`} size="small" />
                <Chip label={`Weight: ${a.weight_percent}%`} size="small" />
                <Chip label={`${stats?.total} students`} size="small" color="info" />
                <Chip label={`${stats?.entered} entered`} size="small" color="success" />
                <Chip label={`${stats?.absent} absent`} size="small" color="warning" />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* Import CSV */}
              <Tooltip title="Download marks CSV template pre-filled with student list">
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate} disabled={!selectedAssessment || !filterClass}>
                  Template
                </Button>
              </Tooltip>
              <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon />}
                disabled={importing || !filterClass}>
                {importing ? <CircularProgress size={16} /> : 'Import CSV'}
                <input type="file" accept=".csv" hidden onChange={handleImportCSV} />
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : 'Save All'}
              </Button>
            </Box>
          </Box>

          {/* Class tabs if assessment spans multiple classes */}
          {entrySheet.assessment.linked_classes?.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>Class:</Typography>
              {entrySheet.assessment.linked_classes.map(c => (
                <Chip key={c.id} label={`${c.name}${c.stream ? ' '+c.stream : ''}`}
                  clickable onClick={() => loadEntrySheet(selectedAssessment, c.id)}
                  color={entrySheet.current_class_id === c.id ? 'primary' : 'default'}
                  variant={entrySheet.current_class_id === c.id ? 'filled' : 'outlined'} />
              ))}
            </Box>
          )}

          {/* Progress */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Entry progress</Typography>
              <Typography variant="caption" fontWeight={700}>{progress}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          {/* Marks table */}
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 70 }}>Adm No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }}>Score (/{maxScore})</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>%</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Absent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entrySheet.students.map((s, idx) => {
                    const m = marks[s.student_id] || {};
                    const raw = parseFloat(m.score);
                    const pct = !isNaN(raw) && !m.is_absent ? ((raw / maxScore) * 100).toFixed(1) : null;

                    return (
                      <TableRow key={s.student_id} hover
                        sx={{ bgcolor: m.is_absent ? 'action.hover' : 'transparent' }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={600}>
                            {s.last_name}, {s.first_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={11} color="text.secondary">{s.admission_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField type="number" size="small"
                            inputProps={{ min: 0, max: maxScore, step: 0.5 }}
                            value={m.is_absent ? '' : (m.score ?? '')}
                            disabled={m.is_absent}
                            onChange={e => handleMarkChange(s.student_id, 'score', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          {m.is_absent
                            ? <Chip label="ABS" size="small" color="warning" />
                            : pct !== null
                            ? <Typography fontWeight={700} fontSize={13}
                                color={parseFloat(pct) >= (a.pass_mark || 50) ? 'success.main' : 'error.main'}>
                                {pct}%
                              </Typography>
                            : <Typography fontSize={12} color="text.disabled">—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          <Checkbox size="small" checked={!!m.is_absent}
                            onChange={e => handleMarkChange(s.student_id, 'is_absent', e.target.checked)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={m.remarks || ''}
                            onChange={e => handleMarkChange(s.student_id, 'remarks', e.target.value)}
                            placeholder="Optional" sx={{ width: 160 }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll} disabled={saving} size="large">
              {saving ? <CircularProgress size={20} /> : 'Save All Marks'}
            </Button>
          </Box>
        </>
      )}

      {!entrySheet && !loading && selectedAssessment && (
        <Alert severity="info">Select a class to load the marks entry sheet for the chosen assessment.</Alert>
      )}
      {!entrySheet && !loading && !selectedAssessment && (
        <Alert severity="info">
          Use the filters above: select Academic Year → Class → Subject → Assessment, then click Reload.
        </Alert>
      )}
    </Box>
  );
}
